import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CButton, CInput } from '@coreui/react'
import ModelSelect from 'src/components/ModelSelect/ModelSelect'
import { useToken } from 'src/state/ducks/auth/hooks'
import {
  emptyFilterValue,
  filterLabel,
  isFilterValueEmpty,
  pillText,
} from './listState'
import './ListViews.scss'

const useClickOutside = (ref, onOutside, active) => {
  useEffect(() => {
    if (!active) return undefined
    const handle = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onOutside()
    }
    // react-select handles (and prevents) the Escape that closes its menu
    const handleKey = (event) =>
      event.key === 'Escape' && !event.defaultPrevented && onOutside()
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', handleKey)
    }
  }, [ref, onOutside, active])
}

const RangeInputs = ({ type, value, onChange }) => {
  const inputType = type === 'number' ? 'number' : 'date'
  return (
    <div className="filter-popover-range">
      <span>De</span>
      <CInput
        size="sm"
        type={inputType}
        value={value.from || ''}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        data-testid="filter-range-from"
      />
      <span>Até</span>
      <CInput
        size="sm"
        type={inputType}
        value={value.to || ''}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        data-testid="filter-range-to"
      />
    </div>
  )
}

const ChoiceOptions = ({ choices, value, onChange }) => (
  <div className="filter-popover-options">
    {choices.map((choice) => {
      const checked = value.some((v) => String(v) === String(choice.value))
      return (
        <label key={String(choice.value)}>
          <input
            type="checkbox"
            checked={checked}
            onChange={() =>
              onChange(
                checked
                  ? value.filter((v) => String(v) !== String(choice.value))
                  : [...value, choice.value]
              )
            }
          />
          {choice.label}
        </label>
      )
    })}
  </div>
)

/** Popover editing the value of one filter, according to its type. */
const FilterEditor = ({ filter, definition, onApply, onRemove }) => {
  const token = useToken()
  const type = definition?.type
  const [value, setValue] = useState(
    isFilterValueEmpty(filter.value) && type
      ? emptyFilterValue(type)
      : filter.value
  )
  const [labels, setLabels] = useState(filter.labels || {})

  // Once edited, a default filter is a regular one (always applied)
  const { isDefault: _isDefault, ...editedFilter } = filter
  const apply = () =>
    onApply({
      ...editedFilter,
      value,
      type,
      ...(type === 'foreign_key' ? { labels } : {}),
    })

  let input
  if (type === 'boolean') {
    input = (
      <div className="filter-popover-options">
        {[
          [true, 'Sim'],
          [false, 'Não'],
        ].map(([option, label]) => (
          <label key={label}>
            <input
              type="radio"
              checked={value === option}
              onChange={() => setValue(option)}
            />
            {label}
          </label>
        ))}
      </div>
    )
  } else if (['date', 'datetime', 'number'].includes(type)) {
    input = <RangeInputs type={type} value={value} onChange={setValue} />
  } else if (type === 'text') {
    input = (
      <CInput
        size="sm"
        autoFocus
        placeholder="Contém..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && apply()}
      />
    )
  } else if (type === 'foreign_key') {
    input = (
      <ModelSelect
        isMulti
        authToken={token}
        fieldName={filter.field}
        djangoApp={definition.app}
        djangoModel={definition.model}
        value={value}
        onChange={(options) => {
          const selected = options || []
          setValue(selected.map((option) => option.value))
          setLabels(
            selected.reduce(
              (acc, option) => ({ ...acc, [option.value]: option.label }),
              {}
            )
          )
        }}
      />
    )
  } else if (type === 'choice') {
    input = (
      <ChoiceOptions
        choices={definition.choices || []}
        value={value}
        onChange={setValue}
      />
    )
  } else {
    // A filter the list no longer describes (e.g. from an old saved view):
    // it can only be removed.
    input = (
      <small className="text-muted">Filtro indisponível nesta lista.</small>
    )
  }

  return (
    <div className="filter-popover" data-testid="filter-popover">
      <div className="filter-popover-title">
        {filterLabel(filter, definition)}
      </div>
      {input}
      <div className="filter-popover-actions">
        <CButton
          size="sm"
          color="link"
          className="text-danger p-0"
          onClick={onRemove}
        >
          Remover
        </CButton>
        {!!type && (
          <CButton size="sm" color="primary" onClick={apply}>
            Aplicar
          </CButton>
        )}
      </div>
    </div>
  )
}

const FilterPill = ({
  filter,
  definition,
  pending,
  suspended,
  open,
  onOpen,
  onClose,
  onApply,
  onRemove,
}) => {
  const ref = useRef(null)
  useClickOutside(ref, onClose, open)

  return (
    <div className="filter-pill-wrapper" ref={ref}>
      <button
        type="button"
        className={`filter-pill ${pending ? 'filter-pill-pending' : ''} ${
          suspended ? 'filter-pill-suspended' : ''
        }`}
        onClick={() => (open ? onClose() : onOpen())}
        title={
          suspended
            ? 'Filtro padrão: não é aplicado durante a pesquisa por termo'
            : pending
            ? filterLabel(filter, definition)
            : pillText(filter, definition)
        }
        data-testid="filter-pill"
      >
        <span className="filter-pill-text">
          {pending
            ? filterLabel(filter, definition)
            : pillText(filter, definition)}
        </span>
        <span className="filter-pill-caret">▼</span>
      </button>
      {open && (
        <FilterEditor
          filter={filter}
          definition={definition}
          onApply={onApply}
          onRemove={onRemove}
        />
      )}
    </div>
  )
}

const AddFilterMenu = ({ definitions, onSelect }) => {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  useClickOutside(ref, () => setOpen(false), open)

  const visible = definitions.filter((definition) =>
    definition.label.toLowerCase().includes(search.trim().toLowerCase())
  )

  return (
    <div className="filter-pill-wrapper" ref={ref}>
      <button
        type="button"
        className="filter-add-button"
        onClick={() => setOpen(!open)}
      >
        + Filtro
      </button>
      {open && (
        <div className="filter-popover" data-testid="filter-field-menu">
          {definitions.length > 7 && (
            <CInput
              size="sm"
              autoFocus
              className="filter-field-search"
              placeholder="Buscar campo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className="filter-popover-options">
            {visible.map((definition) => (
              <button
                type="button"
                key={definition.name}
                className="filter-field-option"
                onClick={() => {
                  setOpen(false)
                  setSearch('')
                  onSelect(definition)
                }}
              >
                {filterLabel({ field: definition.name }, definition)}
              </button>
            ))}
            {!visible.length && (
              <small className="text-muted">Nenhum campo encontrado</small>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Active filters of a list as pills ("Status: Aberta ▼"), each one opening
 * an editor for its value, plus the "+ Filtro" menu with the remaining
 * fields the list can be filtered on.
 *
 * `definitions` is the `filters` description sent by the backend list.
 * While `searching`, the list's default filters show faded: they don't
 * apply to a text search (see `appliedFilters`).
 */
const FilterBar = ({
  definitions = [],
  filters = [],
  onChange,
  searching = false,
}) => {
  const [openField, setOpenField] = useState(null)
  // Filter being added: shown as a dashed pill until a value is applied
  const [pending, setPending] = useState(null)

  const definitionsByName = useMemo(
    () =>
      definitions.reduce(
        (acc, definition) => ({ ...acc, [definition.name]: definition }),
        {}
      ),
    [definitions]
  )

  const shown = pending ? [...filters, pending] : filters
  const available = definitions.filter(
    (definition) => !shown.some((filter) => filter.field === definition.name)
  )

  const close = () => {
    setOpenField(null)
    setPending(null)
  }

  const apply = (filter) => {
    close()
    const exists = filters.some((f) => f.field === filter.field)
    if (isFilterValueEmpty(filter.value)) {
      if (exists) onChange(filters.filter((f) => f.field !== filter.field))
      return
    }
    onChange(
      exists
        ? filters.map((f) => (f.field === filter.field ? filter : f))
        : [...filters, filter]
    )
  }

  const remove = (field) => {
    close()
    if (filters.some((f) => f.field === field))
      onChange(filters.filter((f) => f.field !== field))
  }

  return (
    <>
      {shown.map((filter) => (
        <FilterPill
          key={filter.field}
          filter={filter}
          definition={definitionsByName[filter.field]}
          pending={!!pending && pending.field === filter.field}
          suspended={searching && !!filter.isDefault}
          open={openField === filter.field}
          onOpen={() => setOpenField(filter.field)}
          onClose={close}
          onApply={apply}
          onRemove={() => remove(filter.field)}
        />
      ))}
      {available.length > 0 && (
        <AddFilterMenu
          definitions={available}
          onSelect={(definition) => {
            setPending({
              field: definition.name,
              value: emptyFilterValue(definition.type),
            })
            setOpenField(definition.name)
          }}
        />
      )}
      {filters.length > 0 && (
        <button
          type="button"
          className="filter-clear-button"
          onClick={() => {
            close()
            onChange([])
          }}
        >
          Limpar filtros
        </button>
      )}
    </>
  )
}

export default FilterBar
